import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useContentAccessRules, useAccessTiers } from '../../../hooks/usePremiumAccess';

const { FiLock, FiUnlock, FiSave, FiLoader, FiCalendar, FiDollarSign, FiShield } = FiIcons;

const inputCls = 'w-full glass-input rounded-xl px-3 py-2.5 text-sm focus:outline-none';

const ContentAccessRuleEditor = ({ contentId, contentType = 'exclusive', artistId, onClose }) => {
  const { rules, loading, upsertRule } = useContentAccessRules(contentId, contentType);
  const { tiers } = useAccessTiers(artistId);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const existing = rules[0] || {};
  const [form, setForm] = useState({
    access_tier_id: '',
    min_amount: '0',
    requires_subscription: false,
    expires_at: '',
    geo_restrictions: '',
  });

  useEffect(() => {
    if (existing.id) {
      setForm({
        access_tier_id: existing.access_tier_id || '',
        min_amount: String(existing.min_amount || 0),
        requires_subscription: existing.requires_subscription || false,
        expires_at: existing.expires_at ? existing.expires_at.slice(0, 16) : '',
        geo_restrictions: (existing.geo_restrictions || []).join(', '),
      });
    }
  }, [rules]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    await upsertRule({
      access_tier_id: form.access_tier_id || null,
      min_amount: parseFloat(form.min_amount) || 0,
      requires_subscription: form.requires_subscription,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      geo_restrictions: form.geo_restrictions.split(',').map(s => s.trim()).filter(Boolean),
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-white/60 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5 p-5 rounded-2xl glass-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(100,140,255,0.12)', border: '1px solid rgba(100,140,255,0.2)' }}>
            <SafeIcon icon={FiShield} className="text-sm" style={{ color: 'rgba(150,180,255,0.9)' }} />
          </div>
          <p className="text-[11px] font-black uppercase tracking-[0.12em] text-white">Access Rules</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-white/60 transition-colors">
            Close
          </button>
        )}
      </div>

      {/* Access Tier */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-black uppercase tracking-widest text-white/35">Required Access Tier</label>
        <select value={form.access_tier_id} onChange={e => set('access_tier_id', e.target.value)} className={inputCls}>
          <option value="">— No tier required (open) —</option>
          {tiers.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <p className="text-[10px] font-bold" style={{ color: 'rgba(255,255,255,0.2)' }}>Only fans with this tier can access the content</p>
      </div>

      {/* Min amount */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-black uppercase tracking-widest text-white/35">Minimum Payment (USD)</label>
        <div className="relative">
          <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-white/30" />
          <input type="number" min="0" step="0.01" value={form.min_amount} onChange={e => set('min_amount', e.target.value)}
            className={`${inputCls} pl-9`} placeholder="0.00" />
        </div>
      </div>

      {/* Subscription requirement */}
      <div className="flex items-center gap-3 p-3 rounded-xl badge-glass">
        <button onClick={() => set('requires_subscription', !form.requires_subscription)}
          className={`w-10 h-5 rounded-full transition-all flex items-center px-0.5 flex-shrink-0 ${
            form.requires_subscription ? 'toggle-on justify-end' : 'toggle-off justify-start'
          }`}>
          <div className="w-4 h-4 rounded-full bg-white shadow" />
        </button>
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.12em] text-white">Requires Active Subscription</p>
          <p className="text-[10px] font-bold" style={{ color: 'rgba(255,255,255,0.25)' }}>Fan must have an active membership tier</p>
        </div>
      </div>

      {/* Expiry */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-black uppercase tracking-widest text-white/35 flex items-center gap-1.5">
          <SafeIcon icon={FiCalendar} className="text-xs" /> Access Expires At (optional)
        </label>
        <input type="datetime-local" value={form.expires_at} onChange={e => set('expires_at', e.target.value)} className={inputCls} />
        <p className="text-[10px] font-bold" style={{ color: 'rgba(255,255,255,0.2)' }}>Leave blank for permanent access</p>
      </div>

      {/* Geo restrictions */}
      <div className="space-y-1.5">
        <label className="text-[10px] font-black uppercase tracking-widest text-white/35">Geo Restrictions</label>
        <input value={form.geo_restrictions} onChange={e => set('geo_restrictions', e.target.value)}
          className={inputCls} placeholder="e.g. US, GB, CA (comma separated)" />
        <p className="text-[10px] font-bold" style={{ color: 'rgba(255,255,255,0.2)' }}>Leave blank to allow all regions</p>
      </div>

      {/* Summary badge */}
      <div className="flex items-center gap-2 p-3 rounded-xl badge-glass shimmer">
        <SafeIcon icon={form.access_tier_id || form.requires_subscription || parseFloat(form.min_amount) > 0 ? FiLock : FiUnlock}
          className={`text-sm ${form.access_tier_id || form.requires_subscription ? 'text-blue-300' : 'text-emerald-400'}`} />
        <p className="text-[10px] font-black uppercase tracking-widest text-white/60">
          {!form.access_tier_id && !form.requires_subscription && parseFloat(form.min_amount) === 0
            ? 'Open access — anyone can unlock'
            : [
                form.access_tier_id && `Tier: ${tiers.find(t => t.id === form.access_tier_id)?.name}`,
                form.requires_subscription && 'Subscription required',
                parseFloat(form.min_amount) > 0 && `Min $${parseFloat(form.min_amount).toFixed(2)}`,
              ].filter(Boolean).join(' · ')
          }
        </p>
      </div>

      <button onClick={handleSave} disabled={saving}
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest disabled:opacity-50 ${
          saved ? 'btn-primary' : 'btn-cobalt'
        }`}>
        {saving ? (
          <SafeIcon icon={FiLoader} className="animate-spin text-sm" />
        ) : saved ? (
          <SafeIcon icon={FiSave} className="text-sm" />
        ) : (
          <SafeIcon icon={FiSave} className="text-sm" />
        )}
        {saved ? 'Saved ✓' : 'Save Rules'}
      </button>
    </div>
  );
};

export default ContentAccessRuleEditor;